import { Card, CardContent, Stack, Typography } from '@mui/joy';
import { Check, Copy } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { Button, SlidingPanel } from 'core/components';
import { LabeledValue, Loader } from 'core/elements';
import { useCopyToClipboard } from 'core/hooks';
import { formatDate } from 'core/utils';
import { useGetSubmitOrderLogEntryDetail } from 'dashboard/hooks';
import { ISubmitOrderLogEntry } from 'dashboard/types';
import { Color, Direction, Level, Variant } from 'theme/enums';

import { ReportError } from '../../ReportError';
import { payloadToText } from '../utils';

const contentStyle = { paddingBottom: '24px', width: '820px' };

// Wrapped, never clipped: the bodies are what the reader came for, and the panel
// (not an inner scroller) is what scrolls.
const bodyStyle = {
  fontFamily: 'monospace',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

interface IProps {
  entry: ISubmitOrderLogEntry;
  isOpen: boolean;
  onClose: () => void;
}

export const PayloadPanel = ({ entry, isOpen, onClose }: IProps) => {
  const { t } = useTranslation();

  // Read here rather than off the row: the bodies run to tens of KB per entry, so
  // the list endpoint doesn't carry them and they are fetched one row at a time.
  // Only while the panel is open — an unopened row must not fetch anything.
  const { detail, isLoading, isError, refetch } = useGetSubmitOrderLogEntryDetail(
    isOpen ? entry.id : null
  );

  const requestText = detail?.requestBody == null ? '' : payloadToText(detail.requestBody);
  const responseText = detail?.responseBody == null ? '' : payloadToText(detail.responseBody);

  const getSubtitle = () =>
    [
      entry.companyName,
      entry.orderId && `${t('headerOrderId')} ${entry.orderId}`,
      formatDate(entry.createdAt, true),
    ]
      .filter(Boolean)
      .join(' · ');

  const getBody = (text: string, emptyMessage: string) => {
    if (!text) {
      return <Typography level={Level.BODY_MD}>{emptyMessage}</Typography>;
    }

    return (
      <Card variant={Variant.SOFT} color={Color.NEUTRAL}>
        <CardContent>
          <Typography level={Level.BODY_SM} sx={bodyStyle}>
            {text}
          </Typography>
        </CardContent>
      </Card>
    );
  };

  const getContent = () => {
    if (isLoading) {
      return <Loader />;
    }

    if (isError) {
      return (
        <ReportError
          title={t('errorTitleSubmitOrderPayload')}
          message={t('messageSubmitOrderPayloadError')}
          onRetry={refetch}
        />
      );
    }

    return (
      <Stack sx={{ gap: 3 }}>
        <Stack sx={{ gap: 1.5 }}>
          <Typography level={Level.TITLE_MD}>{t('titleRequest')}</Typography>

          <LabeledValue label={t('labelRequestUrl')} value={detail?.requestUrl} />

          {/* The same case the copy action reports as a failure — here it is a
              visible explanation instead of an empty code block. */}
          {getBody(requestText, t('messageNoSubmitOrderPayload'))}
        </Stack>

        <Stack sx={{ gap: 1.5 }}>
          <Typography level={Level.TITLE_MD}>{t('titleResponse')}</Typography>

          <LabeledValue label={t('headerHttpStatus')} value={detail?.responseStatus} />

          {/* Empty for the no-response outcomes (the call never came back), which
              is itself what someone opening this panel is looking for. */}
          {getBody(responseText, t('messageNoSubmitOrderResponse'))}
        </Stack>
      </Stack>
    );
  };

  return (
    <SlidingPanel
      isOpen={isOpen}
      onClosePanel={onClose}
      title={t('titlePayload')}
      subtitle={getSubtitle()}
      contentStyle={contentStyle}
      autoWidth
      // In the sticky header rather than the panel's footer slot. Both slots are sticky,
      // so this is about placement and not reach: the copy actions sit next to the title
      // and subtitle identifying the entry they act on.
      extra={
        <Stack direction={Direction.ROW} sx={{ gap: 1 }}>
          <CopyButton label={t('buttonLabelCopyRequest')} text={requestText} />

          <CopyButton label={t('buttonLabelCopyResponse')} text={responseText} />
        </Stack>
      }
    >
      {getContent()}
    </SlidingPanel>
  );
};

// One hook call per body: each button owns its "Copied!" state, so copying the
// request does not flash success on the response button.
const CopyButton = ({ label, text }: { label: string; text: string }) => {
  const { isCopied, copy } = useCopyToClipboard();

  const { t } = useTranslation();

  return (
    <Button
      color={isCopied ? Color.SUCCESS : Color.NEUTRAL}
      variant={Variant.OUTLINED}
      text={isCopied ? t('buttonLabelCopied') : label}
      startDecorator={isCopied ? <Check /> : <Copy />}
      disabled={!text}
      onClick={() => copy(text)}
    />
  );
};
